import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Gamepad2, Shield, CreditCard, Mail, MessageCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AmpPage = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [ampEnabled, setAmpEnabled] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [brandsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/brands`),
        axios.get(`${API}/settings`)
      ]);
      setBrands(brandsRes.data);
      setSettings(settingsRes.data);
      
      // Check if AMP is enabled
      if (settingsRes.data.amp_enabled === false) {
        setAmpEnabled(false);
      }
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  // If AMP is disabled, show message and redirect
  if (!loading && !ampEnabled) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-white text-xl font-bold mb-2">AMP Modu Kapalı</h1>
          <p className="text-zinc-400 mb-6">
            AMP mobil sayfa şu anda devre dışı. Ana sayfaya yönlendiriliyorsunuz...
          </p>
          <button
            onClick={() => navigate("/")}
            className="bg-green-600 text-black font-bold px-6 py-3 rounded-lg hover:bg-green-500 transition-colors"
          >
            Ana Sayfaya Git
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] amp-page">
      {/* AMP Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 p-4 sticky top-0 z-40">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <h1 className="font-chivo font-black text-xl text-white" data-testid="amp-site-title">
            {settings.site_name || "Casino İnceleme"}
          </h1>
          <span className="text-xs bg-orange-600 text-white px-2 py-1 rounded font-bold">
            AMP
          </span>
        </div>
      </header>

      {/* Casino List */}
      <main className="p-4 max-w-lg mx-auto">
        <div className="flex flex-col gap-4" data-testid="amp-casino-list">
          {loading ? (
            <div className="text-center py-12 text-zinc-500">Yükleniyor...</div>
          ) : brands.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">Henüz casino eklenmemiş</div>
          ) : (
            brands.map((brand, index) => (
              brand.is_expired ? (
                <AmpExpiredSlot key={brand.id} order={index + 1} settings={settings} />
              ) : (
                <AmpCasinoRow key={brand.id} brand={brand} order={index + 1} settings={settings} />
              )
            ))
          )}
        </div>
      </main>

      {/* AMP Footer */}
      <footer className="bg-zinc-900 border-t border-zinc-800 p-6 mt-8 text-center">
        <p className="text-zinc-500 text-sm">
          {settings.site_name || "Casino İnceleme"} © {new Date().getFullYear()}
        </p>
        <p className="text-zinc-600 text-xs mt-2">
          AMP Mobil Sürüm • <a href="/" className="text-green-500 hover:underline">Tam Sürüm</a>
        </p>
        {settings.footer_text && (
          <p className="text-zinc-600 text-xs mt-1">{settings.footer_text}</p>
        )}
      </footer>
    </div>
  );
};

const AmpCasinoRow = ({ brand, order, settings }) => {
  const ctaText = settings?.cta_button_text || "BONUS AL";

  const handleClick = async () => {
    try {
      await axios.post(`${API}/track-click`, {
        brand_id: brand.id,
        click_type: "affiliate"
      });
    } catch (e) {
      console.error("Click tracking failed:", e);
    }
  };

  return (
    <div 
      className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4 flex flex-col gap-4"
      data-testid={`amp-casino-row-${brand.id}`}
    >
      {/* Header with rank and logo */}
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center text-black font-bold text-lg shrink-0">
          {order}
        </div>
        <div className="w-14 h-14 bg-black rounded-lg border border-zinc-700 flex items-center justify-center p-2 shrink-0">
          {brand.logo_url ? (
            <img 
              src={`${BACKEND_URL}${brand.logo_url}`} 
              alt={brand.name}
              className="max-w-full max-h-full object-contain"
              loading="lazy"
            />
          ) : (
            <span className="text-zinc-500 text-xl">{brand.name?.charAt(0)}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-chivo font-bold text-white text-base truncate">{brand.name}</h2>
          <p className="text-yellow-500 font-bold text-sm truncate">{brand.bonus}</p>
        </div>
      </div>

      {/* Meta info */}
      <div className="flex flex-wrap gap-2 text-xs text-zinc-400">
        <span className="flex items-center gap-1 bg-zinc-800 px-2 py-1 rounded">
          <Shield className="h-3 w-3" />
          {brand.license}
        </span>
        {brand.min_deposit && (
          <span className="flex items-center gap-1 bg-zinc-800 px-2 py-1 rounded">
            <CreditCard className="h-3 w-3" />
            Min: {brand.min_deposit}
          </span>
        )}
      </div>

      {/* Payment methods */}
      {brand.payment_methods && brand.payment_methods.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {brand.payment_methods.slice(0, 4).map((method, idx) => (
            <span key={idx} className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded">
              {method}
            </span>
          ))}
          {brand.payment_methods.length > 4 && (
            <span className="text-xs bg-zinc-800 text-zinc-500 px-2 py-0.5 rounded">
              +{brand.payment_methods.length - 4}
            </span>
          )}
        </div>
      )}

      {/* CTA Button */}
      <a
        href={brand.affiliate_link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="w-full bg-green-600 text-black font-chivo font-bold text-center py-3 rounded-lg hover:bg-green-500 transition-colors active:scale-95"
        data-testid={`amp-play-now-${brand.id}`}
      >
        {ctaText}
      </a>
    </div>
  );
};

const AmpExpiredSlot = ({ order, settings }) => {
  return (
    <div 
      className="bg-zinc-900/50 border-2 border-dashed border-yellow-600/50 rounded-xl p-4 flex flex-col gap-4"
      data-testid={`amp-expired-slot-${order}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-yellow-600 rounded-lg flex items-center justify-center text-black font-bold text-lg shrink-0">
          {order}
        </div>
        <div className="w-14 h-14 bg-zinc-800 rounded-lg border border-dashed border-yellow-600 flex items-center justify-center shrink-0">
          <span className="text-yellow-600 text-2xl">?</span>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-chivo font-bold text-yellow-500 text-base">Bu Alan Kiralanabilir</h2>
          <p className="text-zinc-500 text-sm">Markanızı öne çıkarın!</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {settings.contact_email && (
          <a 
            href={`mailto:${settings.contact_email}`}
            className="flex items-center gap-1 text-zinc-400 hover:text-yellow-500 text-sm"
          >
            <Mail className="h-4 w-4" />
            E-posta
          </a>
        )}
        {settings.whatsapp_link && (
          <a 
            href={settings.whatsapp_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-zinc-400 hover:text-green-500 text-sm"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        )}
        {settings.telegram_link && (
          <a 
            href={settings.telegram_link}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-zinc-400 hover:text-blue-500 text-sm"
          >
            <MessageCircle className="h-4 w-4" />
            Telegram
          </a>
        )}
      </div>
    </div>
  );
};

export default AmpPage;
